import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Autocomplete, Box, Chip, TextField, Typography } from "@mui/material";
import { debounce } from "lodash-es";

import type {
  DrFinderAssessingOrganisation,
  UUID,
} from "@thalamos/aspire-common";

import { api } from "../../api.js";
import { ManagedLocationsContext } from "../../Contexts.js";
import { LoadingSpinner } from "~/components/design-system/index.js";

/** The organisations that sit above AMHP teams, and so organise assessments. */
const ORGANISING_ROLE = "nhs/local-authority";

/** How many organisations one search offers; a narrower query finds the rest. */
const SEARCH_LIMIT = 20;

/**
 * An organisation the picker can list. Those already under this authority say
 * whether it is their billing authority; search results say which authority
 * each bills under now.
 */
export type OrganisationOption = Omit<
  DrFinderAssessingOrganisation,
  "billing"
> & {
  billing?: boolean;
  organisingLocalAuthorityId?: UUID | null;
};

/** The organisations chosen for one authority, as loaded and as edited. */
export type AssessingOrganisationsSelection =
  | { state: "loading" }
  | { state: "failed" }
  | {
      state: "ready";
      initialIds: string[];
      selected: OrganisationOption[];
    };

/**
 * The saves that bring the authority's organisations to the selection, split
 * around the save of its details. Each replaces the authority's whole set.
 */
export type AssessingOrganisationsSaves = {
  /**
   * The organisations kept, or null when none is removed. Saved before the
   * details, as an authority's ICB cannot change while it shares an
   * organisation with another authority.
   */
  removals: string[] | null;
  /**
   * The whole selection, or null when none is added. Saved after the details,
   * as an organisation added is checked against the authority's ICB.
   */
  additions: string[] | null;
};

/**
 * The saves for the authority's organisations. A selection that failed to load
 * is never saved, as its empty list would unlink every organisation.
 */
export function assessingOrganisationsSaves(
  selection: AssessingOrganisationsSelection,
): AssessingOrganisationsSaves {
  if (selection.state !== "ready") {
    return { removals: null, additions: null };
  }

  const selectedIds = selection.selected.map(({ id }) => id);
  const keptIds = selection.initialIds.filter((id) => selectedIds.includes(id));
  const removesAny = keptIds.length < selection.initialIds.length;
  const addsAny = selectedIds.some((id) => !selection.initialIds.includes(id));

  return {
    removals: removesAny ? keptIds : null,
    additions: addsAny ? selectedIds : null,
  };
}

/**
 * Loads the organisations under `localAuthorityId` and holds the edited
 * selection. A different authority's list must never be shown, or saved, for
 * this one, so switching authority starts again from loading.
 */
export function useAssessingOrganisationsSelection(
  localAuthorityId: string,
  loadOrganisations: () => Promise<OrganisationOption[] | null>,
) {
  const [selection, setSelection] = useState<AssessingOrganisationsSelection>({
    state: "loading",
  });
  const [loadedFor, setLoadedFor] = useState(localAuthorityId);
  if (loadedFor !== localAuthorityId) {
    setLoadedFor(localAuthorityId);
    setSelection({ state: "loading" });
  }

  useEffect(() => {
    let cancelled = false;
    void loadOrganisations().then((organisations) => {
      if (cancelled) {
        return;
      }
      setSelection(
        organisations === null
          ? { state: "failed" }
          : {
              state: "ready",
              initialIds: organisations.map(({ id }) => id),
              selected: organisations,
            },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [loadOrganisations]);

  const setSelected = (selected: OrganisationOption[]) => {
    setSelection((current) =>
      current.state === "ready" ? { ...current, selected } : current,
    );
  };

  return { selection, setSelected };
}

export interface DrFinderAssessingOrganisationsPickerInnerProps {
  localAuthorityId: string;
  selection: AssessingOrganisationsSelection;
  onChange: (selected: OrganisationOption[]) => void;
  searchOrganisations: (query: string) => Promise<OrganisationOption[]>;
  authorityNameOf: (localAuthorityId: string) => string | undefined;
}

/**
 * Chooses every {@link DrFinderAssessingOrganisation} of one local authority.
 * The selection is saved by the form it sits in.
 */
export function DrFinderAssessingOrganisationsPickerInner({
  localAuthorityId,
  selection,
  onChange,
  searchOrganisations,
  authorityNameOf,
}: DrFinderAssessingOrganisationsPickerInnerProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<OrganisationOption[]>([]);
  const latestQuery = useRef("");

  // A search for one authority is not carried over to the next.
  const [searchedFor, setSearchedFor] = useState(localAuthorityId);
  if (searchedFor !== localAuthorityId) {
    setSearchedFor(localAuthorityId);
    setQuery("");
    setResults([]);
  }

  const debouncedSearch = useMemo(
    () =>
      debounce(async (trimmed: string) => {
        const found = await searchOrganisations(trimmed);
        // A slower earlier search must not replace the results of a later one.
        if (trimmed === latestQuery.current) {
          setResults(found);
        }
      }, 300),
    [searchOrganisations],
  );

  useEffect(() => {
    const trimmed = query.trim();
    latestQuery.current = trimmed;
    if (trimmed.length < 2) {
      setResults([]);
      return;
    }
    void debouncedSearch(trimmed);
    return () => {
      debouncedSearch.cancel();
    };
  }, [query, debouncedSearch]);

  if (selection.state === "loading") {
    return <LoadingSpinner label="Loading organisations..." />;
  }

  if (selection.state === "failed") {
    return (
      <Typography variant="body2" color="error" sx={{ mt: 2 }}>
        The organisations under this local authority could not be loaded.
      </Typography>
    );
  }

  const selected = selection.selected;
  const selectedIds = selected.map(({ id }) => id);
  const options = [
    ...selected,
    ...results.filter(({ id }) => !selectedIds.includes(id)),
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Dr Finder assessing organisations
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 2 }}>
        NHS/Local Authority organisations whose AMHP teams arrange assessments
        on behalf of this local authority. An organisation can be under several
        local authorities in the same ICB, and bills under the one marked
        Billing. When it assesses a patient from outside that ICB, the
        ICB&apos;s policy decides whether Dr Finder Payments bills the
        patient&apos;s area or the billing local authority, at its own rates.
      </Typography>
      <Autocomplete
        multiple
        options={options}
        value={selected}
        // Options come from a server-side search, so they are not filtered again.
        filterOptions={(unfiltered) => unfiltered}
        inputValue={query}
        onInputChange={(_event, value) => {
          setQuery(value);
        }}
        onChange={(_event, newValue) => {
          onChange(newValue);
        }}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const billingAuthority =
            option.organisingLocalAuthorityId &&
            option.organisingLocalAuthorityId !== localAuthorityId
              ? authorityNameOf(option.organisingLocalAuthorityId)
              : undefined;
          return (
            <li key={key} {...optionProps}>
              <Box>
                <Typography variant="body2">{option.name}</Typography>
                {billingAuthority !== undefined && (
                  <Typography variant="caption" color="text.secondary">
                    Billed under {billingAuthority}
                  </Typography>
                )}
              </Box>
            </li>
          );
        }}
        renderValue={(value, getItemProps) =>
          value.map((option, index) => {
            const { key, ...itemProps } = getItemProps({ index });
            return (
              <Chip
                key={key}
                {...itemProps}
                label={
                  option.billing === true
                    ? `${option.name} · Billing`
                    : option.name
                }
                size="small"
              />
            );
          })
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label="Organisations"
            placeholder="Search organisations..."
            size="small"
          />
        )}
        sx={{ mb: 2 }}
      />
    </Box>
  );
}

/**
 * The API calls that {@link DrFinderAssessingOrganisationsPickerInner} makes.
 */
export interface AssessingOrganisationsApi {
  loadOrganisations: () => Promise<OrganisationOption[] | null>;
  searchOrganisations: (query: string) => Promise<OrganisationOption[]>;
  /** Resolves to the reason the save was refused, or null once saved. */
  saveOrganisations: (organisationIds: string[]) => Promise<string | null>;
  authorityNameOf: (localAuthorityId: string) => string | undefined;
}

/** {@link AssessingOrganisationsApi} for one authority, over the server API. */
export function useAssessingOrganisationsApi(
  localAuthorityId: string,
): AssessingOrganisationsApi {
  const { managedLocations } = useContext(ManagedLocationsContext);

  const loadOrganisations = useMemo(
    () => async () => {
      try {
        const response =
          await api.organisations.getDrFinderAssessingOrganisations(
            localAuthorityId,
          );
        return response.status === 200 ? response.data.organisations : null;
      } catch {
        return null;
      }
    },
    [localAuthorityId],
  );

  const searchOrganisations = useMemo(
    () => async (query: string) => {
      try {
        const response = await api.organisations.search(
          query,
          SEARCH_LIMIT,
          0,
          ORGANISING_ROLE,
        );
        return response.status === 200 ? response.data.results : [];
      } catch {
        return [];
      }
    },
    [],
  );

  const saveOrganisations = async (organisationIds: string[]) => {
    const failed = "The organisations could not be saved";
    try {
      const response =
        await api.organisations.setDrFinderAssessingOrganisations(
          localAuthorityId,
          { organisationIds },
        );
      if (response.status === 204) {
        return null;
      }
      return typeof response.data === "object" ? response.data.reason : failed;
    } catch {
      return failed;
    }
  };

  return {
    loadOrganisations,
    searchOrganisations,
    saveOrganisations,
    authorityNameOf: (id) =>
      managedLocations.find((location) => location.id === id)?.name,
  };
}
